import React from 'react';
import { Group, Box } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { EO_BRANDS } from '~/domains/auth/consts';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';
import { VectorRYPIcon } from '~/domains/design-system/vectors/VectorRYPIcon';
import { VectorOFPIcon } from '~/domains/design-system/vectors/VectorOFPIcon';
import { VectorSplashIcon } from '~/domains/design-system/vectors/VectorSplashIcon';

import ContestTypeBadge from '../ContestTypeBadge/ContestTypeBadge';

import useStyles from './ContestRestartCard.styles';


function Divider() {
  const { classes } = useStyles();
  return (
    <Text className={classes.metaDivider} variant="body-small">
      •
    </Text>
  );
}

function StandardMetadata({ contest }: { contest: Contest | MyUserContest; }) {
  const { t } = useTranslation('contest');
  const { theme } = useStyles();

  return (
    <>
      <Text variant="title-small" color={theme.colors.gray2[3]}>
        {new Date(contest.start_date).toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Divider />
      <Text variant="title-small" color={theme.colors.gray2[3]}>
        {contest.league?.name}{' '}
        <ContestTypeBadge type={contest.contest_type} variant="unstyled"/>
      </Text>
      <Divider />
      <Text variant="title-small" color={theme.colors.gray2[3]}>
        {t('common.contestRestartCard.labels.entries')}
      </Text>
      <Text variant="title-small" color={theme.colors.gray2[7]}>
        {contest.entries?.filled}
      </Text>
    </>
  );
}

interface ContestInviteCardContestInfoProps {
  contest: Contest | MyUserContest;
  source: keyof typeof EO_BRANDS;
}
function ContestRestartCardContestInfo({ contest, source }: ContestInviteCardContestInfoProps) {
  const { cx, classes, theme } = useStyles();

  const isRyp = source === EO_BRANDS.RYP;
  const isOfp = source === EO_BRANDS.OFP;
  const isEoBrand = isRyp || isOfp;

  return (
    <Group className={classes.contestSection} spacing="sm">
      <div className={cx(classes.brandAvatar, {
        brandRyp: isRyp,
        brandOfp: isOfp,
        brandSplash: !isEoBrand,
        })}>
        {isRyp && <VectorRYPIcon color={theme.colors.gray2[4]} height={15} />}
        {isOfp && <VectorOFPIcon color={theme.colors.gray2[4]} height={15} />}
        {!isEoBrand && <VectorSplashIcon color={theme.white} height={13} />}
      </div>
      <Box className={classes.contestInfo}>
        <Title variant="headline-medium" className={classes.contestName}>
          {contest.name}
        </Title>
        <Group className={classes.contestMeta}>
          <StandardMetadata contest={contest} />
        </Group>
      </Box>
    </Group>
  );
}

export default ContestRestartCardContestInfo;
